import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import { formatMoney } from "../../lib/api";
import { MoneyAmount } from "../../components/MoneyAmount";
import { LineItemsView } from "../../components/LineItemsView";
import { TagChips } from "../../components/Tags";
import { useHistory, useProfile, useMovementsMutations, useTopTags, useCashAccounts } from "../../hooks/queries";
import { useQuery } from "@tanstack/react-query";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { toast } from "sonner";
import {
  ArrowDownCircle, ArrowUpCircle, ArrowRightLeft, History as HistoryIcon, ChevronLeft, ChevronRight,
  Loader2, Receipt, Wallet, TrendingUp, TrendingDown, Scale, Trash2, Pencil, Search, Calendar as CalendarIcon, List, X, CalendarClock, SlidersHorizontal, CreditCard,
} from "lucide-react";
import { PageHeader } from "../../components/PageHeader";
import { SwipeActions } from "../../components/SwipeActions";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { api } from "../../lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import MovementEditDialog from "../../components/MovementEditDialog";
import { AttachmentBadge } from "../../components/AttachmentBadge";
import { AccountMissingBadge } from "../../components/AccountMissingBadge";
import { AccountChip } from "../../components/AccountChip";
import { useMovementHighlight } from "../../lib/highlights";
import { GroupedListView } from "../../components/GroupedListView";
import { useTransactionsFilters } from "../../hooks/useTransactionsFilters";
import { RestoredFiltersBanner } from "../../components/RestoredFiltersBanner";

const monthRange = (year, monthIdx) => {
  const start = new Date(Date.UTC(year, monthIdx, 1, 0, 0, 0));
  const end = new Date(Date.UTC(year, monthIdx + 1, 1, 0, 0, 0));
  return { start: start.toISOString(), end: end.toISOString() };
};

const monthLabel = (lang, year, monthIdx) =>
  new Date(Date.UTC(year, monthIdx, 1)).toLocaleString(tx(lang, "en", "es", "es"), {
    month: "long",
    year: "numeric",
  });

/** ISO date string in user-local timezone (YYYY-MM-DD) for stable bucketing. */
const localDateKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** ISO week bucket key (YYYY-Www). */
const weekKey = (iso) => {
  const d = new Date(iso);
  // Move to Monday-of-the-week
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  return `${d.getFullYear()}-W${String(getISOWeek(d)).padStart(2, "0")}`;
};
function getISOWeek(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
}

const monthKey = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function History() {
  const { lang, t } = useLang();
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";

  const now = useMemo(() => new Date(), []);
  const [cursor, setCursor] = useState({ year: now.getUTCFullYear(), month: now.getUTCMonth() });
  const { start, end } = useMemo(() => monthRange(cursor.year, cursor.month), [cursor]);
  const { data, isLoading } = useHistory(start, end);

  // Filters: shared state via useTransactionsFilters (search/tag/account/
  // groupBy); page-specific (searchAmount + typeFilter) stay local.
  // iter-213.6 — persist scoped to /history so the user's filter context
  // survives reloads and tab switches.
  const {
    search: searchText, setSearch: setSearchText,
    tag: tagFilter, setTag: setTagFilter,
    account: accountFilter, setAccount: setAccountFilter,
    groupBy, setGroupBy,
    restored: filtersRestored,
    clear: clearHookFilters,
    hasActive: filtersActive,
  } = useTransactionsFilters({ persistKey: "history.filters" });
  const [searchAmount, setSearchAmount] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  // iter-188 — Honour ?account_id=<id> deep-link from the Cash Accounts
  // page and ?date=YYYY-MM-DD deep-link from the heatmap so the user
  // can jump straight to "show me movements for THIS account / day".
  // One-shot read on mount; subsequent dropdown changes keep owning
  // the state.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    let mutated = false;
    const next = new URLSearchParams(searchParams);
    const aid = searchParams.get("account_id");
    if (aid) {
      setAccountFilter(aid);
      next.delete("account_id");
      mutated = true;
    }
    const dateStr = searchParams.get("date");
    if (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [y, m] = dateStr.split("-").map((n) => parseInt(n, 10));
      if (!Number.isNaN(y) && !Number.isNaN(m)) {
        setCursor({ year: y, month: m - 1 });
        // Pre-populate the search text with the day to scope visually.
        setSearchText(dateStr);
      }
      next.delete("date");
      mutated = true;
    }
    if (mutated) setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [viewMode, setViewMode] = useState("list"); // list | calendar
  const { data: tagsData } = useTopTags();
  const tagSuggestions = (tagsData?.by_kind?.movements || []).map((r) => r.tag);
  const { data: accounts = [] } = useCashAccounts();

  const goPrev = () => {
    const m = cursor.month - 1;
    if (m < 0) setCursor({ year: cursor.year - 1, month: 11 });
    else setCursor({ year: cursor.year, month: m });
  };
  const goNext = () => {
    const m = cursor.month + 1;
    if (m > 11) setCursor({ year: cursor.year + 1, month: 0 });
    else setCursor({ year: cursor.year, month: m });
  };
  const isCurrentMonth =
    cursor.year === now.getUTCFullYear() && cursor.month === now.getUTCMonth();

  const totals = data?.totals || { income: 0, expense: 0, net: 0, movements_count: 0 };
  const allMovements = data?.movements || [];
  const collected = data?.collected_receivables || [];
  const paid = data?.paid_payments || [];

  // Apply filters to movements.
  const movements = useMemo(() => {
    const txt = searchText.trim().toLowerCase();
    const amt = searchAmount ? Number(searchAmount) : null;
    return allMovements.filter((m) => {
      if (typeFilter && m.type !== typeFilter) return false;
      if (tagFilter && !(Array.isArray(m.tags) && m.tags.includes(tagFilter))) return false;
      if (accountFilter && m.account_id !== accountFilter) return false;
      if (txt) {
        const hay = `${m.description || ""} ${(m.tags || []).join(" ")}`.toLowerCase();
        if (!hay.includes(txt)) return false;
      }
      if (amt != null && !Number.isNaN(amt)) {
        // ±2% tolerance so users can type a rough amount
        const tol = Math.max(1, amt * 0.02);
        if (Math.abs(Number(m.amount || 0) - amt) > tol) return false;
      }
      return true;
    });
  }, [allMovements, searchText, searchAmount, typeFilter, tagFilter, accountFilter]);

  const hasActiveFilters = !!(searchText || searchAmount || typeFilter || tagFilter || accountFilter);
  const clearFilters = () => {
    setSearchText(""); setSearchAmount(""); setTypeFilter(""); setTagFilter(""); setAccountFilter("");
    try { clearHookFilters && clearHookFilters(); } catch (e) { /* no-op */ }
  };

  return (
    <Layout>
      <PageHeader
        eyebrow={t("history_scope_badge")}
        title={tx(lang, "Movements", "Movimientos", "Movimentos")}
        subtitle={`${monthLabel(lang, cursor.year, cursor.month)} · ${t("history_scope_sub")}`}
        testid="history-header"
        actions={
          <>
            <Button data-testid="history-prev-btn" variant="outline" size="icon" onClick={goPrev} className="rounded-xl">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button data-testid="history-next-btn" variant="outline" size="icon" onClick={goNext} disabled={isCurrentMonth} className="rounded-xl">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        }
      />

      {/* Totals — 3-up KPI cards in mono numbers per design_guidelines.json */}
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <Card className="lf-surface lf-surface-hover p-4" data-testid="history-total-income">
          <div className="flex items-center justify-between">
            <span className="lf-label">{tx(lang, "Income", "Ingresos", "Ingresos")}</span>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="font-display lf-num text-2xl font-bold mt-1 text-emerald-700">
            +{formatMoney(totals.income, currency)}
          </div>
        </Card>
        <Card className="lf-surface lf-surface-hover p-4" data-testid="history-total-expense">
          <div className="flex items-center justify-between">
            <span className="lf-label">{tx(lang, "Expense", "Gastos", "Gastos")}</span>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </div>
          <div className="font-display lf-num text-2xl font-bold mt-1 text-red-600">
            -{formatMoney(totals.expense, currency)}
          </div>
        </Card>
        <Card className="lf-surface lf-surface-hover p-4" data-testid="history-total-net">
          <div className="flex items-center justify-between">
            <span className="lf-label">{tx(lang, "Net", "Neto", "Neto")}</span>
            <Scale className="h-4 w-4 text-slate-600" />
          </div>
          <div className={`font-display lf-num text-2xl font-bold mt-1 ${totals.net >= 0 ? "text-slate-900" : "text-red-600"}`}>
            {totals.net >= 0 ? "" : "-"}{formatMoney(Math.abs(totals.net), currency)}
          </div>
        </Card>
      </div>

      {isLoading ? (
        <Card className="p-10 rounded-2xl border-slate-200 flex items-center justify-center text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" />
          {tx(lang, "Loading…", "Cargando…", "Cargando…")}
        </Card>
      ) : (
        <Tabs defaultValue="movements" className="w-full">
          <TabsList className="grid grid-cols-3 w-full max-w-xl" data-testid="history-tabs">
            <TabsTrigger value="movements" data-testid="history-tab-movements">
              {tx(lang, "Movements", "Movimientos", "Movimientos")}
              <span className="ml-1.5 text-[11px] text-slate-500 tabular-nums">({movements.length})</span>
            </TabsTrigger>
            <TabsTrigger value="collected" data-testid="history-tab-collected">
              {tx(lang, "Collected", "Cobrados", "Cobrados")}
              <span className="ml-1.5 text-[11px] text-slate-500 tabular-nums">({collected.length})</span>
            </TabsTrigger>
            <TabsTrigger value="paid" data-testid="history-tab-paid">
              {tx(lang, "Paid", "Pagados", "Pagados")}
              <span className="ml-1.5 text-[11px] text-slate-500 tabular-nums">({paid.length})</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="movements" className="mt-4">
            {/* iter-213.4 — Adopt the shared GroupedListView with the
                `extraFilters` slot for type/account/amount selects.
                Matches the pattern already in /receivables and /pagos.
                The mobile bottom-sheet FilterBar was retired: the
                shared filter panel wraps responsively and gives a
                consistent UX across the 3 list pages. */}
            <ViewControls
              lang={lang}
              viewMode={viewMode} setViewMode={setViewMode}
            />
            {viewMode === "list" ? (
              <GroupedListView
                testid="movements"
                lang={lang}
                items={movements}
                isLoading={isLoading}
                renderRow={(m) => <MovementRow key={m.id} m={m} currency={currency} lang={lang} />}
                dateAccessor={(m) => m.date}
                order="desc"
                restoredBanner={
                  <RestoredFiltersBanner
                    visible={filtersRestored && filtersActive}
                    onClear={clearFilters}
                    testid="history"
                    lang={lang}
                  />
                }
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
                search={searchText}
                onSearchChange={setSearchText}
                searchPlaceholder={tx(lang, "Search text…", "Buscar texto…", "Buscar texto…")}
                tagSuggestions={tagSuggestions}
                tagFilter={tagFilter}
                onTagFilterChange={setTagFilter}
                extraFilters={
                  <>
                    <Input
                      data-testid="movements-search-amount"
                      type="number"
                      placeholder={tx(lang, "Amount", "Monto", "Monto")}
                      value={searchAmount}
                      onChange={(e) => setSearchAmount(e.target.value)}
                      className="h-9 text-sm w-[120px]"
                    />
                    <Select
                      value={typeFilter || "_all"}
                      onValueChange={(v) => setTypeFilter(v === "_all" ? "" : v)}
                    >
                      <SelectTrigger className="h-9 text-sm w-[140px]" data-testid="movements-filter-type">
                        <SelectValue placeholder={tx(lang, "Type", "Tipo", "Tipo")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">{tx(lang, "All types", "Todos los tipos", "Todos os tipos")}</SelectItem>
                        <SelectItem value="income">{tx(lang, "Income", "Ingreso", "Ingreso")}</SelectItem>
                        <SelectItem value="expense">{tx(lang, "Expense", "Gasto", "Gasto")}</SelectItem>
                        <SelectItem value="transfer">{tx(lang, "Transfer", "Transferencia", "Transferência")}</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={accountFilter || "_all"}
                      onValueChange={(v) => setAccountFilter(v === "_all" ? "" : v)}
                    >
                      <SelectTrigger className="h-9 text-sm w-[170px]" data-testid="movements-filter-account">
                        <SelectValue placeholder={tx(lang, "All accounts", "Todas las cuentas", "Todas as contas")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">{tx(lang, "All accounts", "Todas las cuentas", "Todas as contas")}</SelectItem>
                        {accounts.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                }
                // eslint-disable-next-line react/no-unstable-nested-components
                groupSummary={(groupItems) => {
                  const income = groupItems.filter((m) => m.type === "income").reduce((s, m) => s + Number(m.amount || 0), 0);
                  const expense = groupItems.filter((m) => m.type === "expense").reduce((s, m) => s + Number(m.amount || 0), 0);
                  const net = income - expense;
                  return (
                    <div className="flex items-center gap-3 text-xs">
                      {income > 0 && <span className="text-emerald-700 tabular-nums font-semibold">+{formatMoney(income, currency)}</span>}
                      {expense > 0 && <span className="text-red-600 tabular-nums font-semibold">-{formatMoney(expense, currency)}</span>}
                      <span className={`tabular-nums font-bold ${net >= 0 ? "text-slate-900" : "text-red-600"}`}>
                        {net >= 0 ? "" : "-"}{formatMoney(Math.abs(net), currency)}
                      </span>
                    </div>
                  );
                }}
                emptyState={<EmptyState lang={lang} kind="movements" />}
              />
            ) : (
              <CalendarView items={movements} currency={currency} lang={lang} year={cursor.year} month={cursor.month} />
            )}
          </TabsContent>
          <TabsContent value="collected" className="mt-4">
            <CollectedList items={collected} currency={currency} lang={lang} />
          </TabsContent>
          <TabsContent value="paid" className="mt-4">
            <PaidList items={paid} currency={currency} lang={lang} />
          </TabsContent>
        </Tabs>
      )}
    </Layout>
  );
}

// =========================================================================
// View controls (list | calendar) + grouping selector
// =========================================================================
function ViewControls({ lang, viewMode, setViewMode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <div className="inline-flex items-center bg-slate-100 rounded-xl p-1" data-testid="movements-view-toggle">
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={() => setViewMode("list")}
          data-testid="movements-view-list"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${viewMode === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <List className="h-3.5 w-3.5" /> {tx(lang, "List", "Lista", "Lista")}
        </button>
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={() => setViewMode("calendar")}
          data-testid="movements-view-calendar"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${viewMode === "calendar" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
        >
          <CalendarIcon className="h-3.5 w-3.5" /> {tx(lang, "Calendar", "Calendario", "Calendario")}
        </button>
      </div>
    </div>
  );
}


// =========================================================================
// Calendar grid view (month)
// =========================================================================
function CalendarView({ items, currency, lang, year, month }) {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  // Build a map: localDateKey -> [items]
  const byDay = {};
  for (const m of items) {
    const k = localDateKey(m.date);
    if (!byDay[k]) byDay[k] = [];
    byDay[k].push(m);
  }

  // Build day cells; pad with leading blanks for first-week alignment (Mon=0).
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, key, rows: byDay[key] || [] });
  }

  const dayNames = tx(lang,
    ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"],
    ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"],
  );

  const [selected, setSelected] = useState(null);
  return (
    <div data-testid="movements-calendar">
      <Card className="p-3 rounded-2xl border-slate-200">
        <div className="grid grid-cols-7 gap-1 mb-1">
          {dayNames.map((d) => (
            <div key={d} className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold text-center py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, idx) => {
            if (!c) return <div key={`blank-${idx}`} className="aspect-square" />;
            const inc = c.rows.filter((m) => m.type === "income").reduce((s, m) => s + Number(m.amount || 0), 0);
            const exp = c.rows.filter((m) => m.type === "expense").reduce((s, m) => s + Number(m.amount || 0), 0);
            const isSelected = selected === c.key;
            return (
              <button /* design-system: ALLOW legacy-todo */
                key={c.key}
                type="button"
                onClick={() => setSelected(isSelected ? null : c.key)}
                data-testid={`cal-day-${c.key}`}
                className={`aspect-square rounded-lg border text-[11px] flex flex-col items-stretch justify-between p-1.5 transition-all ${
                  isSelected ? "bg-emerald-50 border-emerald-400 ring-2 ring-emerald-300" :
                  c.rows.length ? "bg-white border-slate-200 hover:border-emerald-300 hover:bg-emerald-50" :
                  "bg-slate-50 border-slate-100 text-slate-400"
                }`}
              >
                <span className="font-bold text-slate-900 text-left">{c.d}</span>
                {c.rows.length > 0 && (
                  <div className="text-[9px] leading-tight text-left tabular-nums">
                    {inc > 0 && <div className="text-emerald-700 truncate">+{formatMoney(inc, currency)}</div>}
                    {exp > 0 && <div className="text-red-600 truncate">-{formatMoney(exp, currency)}</div>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>
      {selected && byDay[selected] && (
        <div className="mt-4" data-testid="cal-day-selected">
          <h4 className="text-sm font-bold text-slate-900 mb-2">
            {new Date(selected).toLocaleDateString(tx(lang, "en", "es", "es"), { weekday: "long", day: "2-digit", month: "long" })}
          </h4>
          <div className="grid gap-2">
            {byDay[selected].map((m) => <MovementRow key={m.id} m={m} currency={currency} lang={lang} />)}
          </div>
        </div>
      )}
    </div>
  );
}

// =========================================================================
// Reusable empty state + lists for "Cobrados" / "Pagados"
// =========================================================================
function EmptyState({ lang, kind }) {
  const COPY = {
    movements: {
      es: { title: "Sin movimientos en este periodo.", hint: "Cualquier ingreso o gasto que registres (manual o por chat) aparecerá aquí." },
      en: { title: "No movements in this period.", hint: "Any income or expense you log (manual or via chat) will appear here." },
    },
    collected: {
      es: { title: "Sin cobros marcados como recibidos en este periodo.", hint: "Cuando marques un cobro como recibido en Cobros, aparecerá aquí.", to: "/receivables", cta: "Ir a Cobros" },
      en: { title: "No receivables marked as collected in this period.", hint: "When you mark a receivable as collected on the Receivables page, it'll show up here.", to: "/receivables", cta: "Go to Receivables" },
    },
    paid: {
      es: { title: "Sin pagos marcados como pagados en este periodo.", hint: "Cuando marques un pago como pagado en Pagos, aparecerá aquí.", to: "/payments", cta: "Ir a Pagos" },
      en: { title: "No payments marked as paid in this period.", hint: "When you mark a payment as paid on the Payments page, it'll show up here.", to: "/payments", cta: "Go to Payments" },
    },
  };
  const c = COPY[kind][tx(lang, "en", "es", "es")];
  return (
    <Card className="p-8 text-center rounded-2xl border-slate-200 flex flex-col items-center gap-2" data-testid={`history-empty-${kind}`}>
      <div className="font-semibold text-slate-900">{c.title}</div>
      <p className="text-sm text-slate-500 max-w-md">{c.hint}</p>
      {c.to && (
        <a
          href={c.to}
          data-testid={`history-empty-${kind}-cta`}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 hover:text-emerald-800"
        >
          {c.cta} →
        </a>
      )}
    </Card>
  );
}

// =========================================================================
// Single movement row (view-only + delete)
// =========================================================================
function MovementRow({ m, currency, lang }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const { remove } = useMovementsMutations();
  const navigate = useNavigate();
  const isTransfer = m.type === "transfer";
  const isIncome = m.type === "income";
  const isEs = lang === "es";
  const fresh = useMovementHighlight(m);

  const del = async () => {
    try {
      const res = await remove.mutateAsync(m.id);
      const restored = res?.restored;
      if (restored?.kind) {
        // Show what was restored + a quick 'Ir' link to the corresponding
        // list page so the user can verify the undo cycled cleanly.
        toast.success(isEs ? `↩ ${restored.label_es}` : `↩ ${restored.label_en}`, {
          duration: 6000,
          action: restored.link ? {
            label: isEs ? "Ir" : "Go",
            onClick: () => navigate(restored.link),
          } : undefined,
        });
      } else {
        toast.success(isEs ? "Movimiento eliminado" : "Movement deleted");
      }
      setConfirmDelete(false);
    } catch (e) {
      console.error("delete movement failed", e);
    }
  };

  // Resolve account names + currencies for the transfer arrow row.
  const { data: cashAccounts = [] } = useCashAccounts();
  // iter-184-7 — Resolve attached revolving-debt names too so movements
  // funded via a credit card / line of credit can render an inline
  // "Pagado con TC X" chip. Stale time matches the dialogs' picker so
  // edits propagate fast.
  const { data: debts = [] } = useQuery({
    queryKey: ["debts"],
    queryFn: async () => {
      const r = (await api.get("/debts")).data;
      return Array.isArray(r) ? r : (r?.rows || []);
    },
    staleTime: 60_000,
  });
  const debtById = (id) => debts.find((x) => x.id === id);
  const findAccount = (id) => cashAccounts.find((x) => x.id === id);
  const findName = (id) => {
    if (!id) return "";
    const a = findAccount(id);
    return a ? a.name : id.slice(0, 6);
  };
  const srcCurrency = isTransfer
    ? (findAccount(m.account_id)?.currency || currency)
    : currency;
  const dstCurrency = isTransfer
    ? (findAccount(m.to_account_id)?.currency || currency)
    : currency;

  const labelFallback = isTransfer
    ? (isEs ? "Traslado" : "Transfer")
    : isIncome
      ? (isEs ? "Ingreso" : "Income")
      : (isEs ? "Gasto" : "Expense");

  return (
    <SwipeActions
      testid={`history-swipe-${m.id}`}
      actions={[
        {
          label: isEs ? "Editar" : "Edit",
          icon: Pencil,
          color: "bg-sky-500",
          testid: `swipe-edit-history-${m.id}`,
          onClick: () => setShowEdit(true),
        },
        {
          label: isEs ? "Eliminar" : "Delete",
          icon: Trash2,
          color: "bg-rose-500",
          testid: `swipe-delete-history-${m.id}`,
          onClick: () => setConfirmDelete(true),
        },
      ]}
    >
    <Card data-testid={`history-movement-${m.id}`} className={`p-3 sm:p-4 rounded-2xl border-slate-200 group ${fresh ? "row-fresh" : ""}`}>
      <div className="flex items-center justify-between gap-3 min-w-0">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
            isTransfer
              ? "bg-violet-50 text-violet-600"
              : isIncome
                ? "bg-emerald-50 text-emerald-600"
                : "bg-red-50 text-red-600"
          }`}>
            {isTransfer
              ? <ArrowRightLeft className="h-5 w-5" />
              : isIncome
                ? <ArrowDownCircle className="h-5 w-5" />
                : <ArrowUpCircle className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-slate-900 truncate" title={m.description || ""}>
              {m.description || labelFallback}
            </div>
            {isTransfer && (
              <div
                className="text-xs text-slate-600 truncate mt-0.5"
                data-testid={`history-transfer-route-${m.id}`}
              >
                <span className="font-medium">{findName(m.account_id)}</span>
                <span className="mx-1 text-slate-400">→</span>
                <span className="font-medium">{findName(m.to_account_id)}</span>
                {m.fx_source && (
                  <span
                    className="ml-2 text-[10px] inline-flex items-center px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-800 border border-amber-200"
                    data-testid={`history-transfer-fx-${m.id}`}
                    title={isEs ? "Origen de la tasa FX" : "FX rate source"}
                  >
                    {m.fx_source === "manual"
                      ? (isEs ? "manual" : "manual")
                      : (isEs ? "mercado" : "market")}
                  </span>
                )}
              </div>
            )}
            <div className="text-xs text-slate-500 truncate">
              {new Date(m.date).toLocaleString()}
              {m.source && m.source !== "manual" && (
                <Badge variant="outline" className="ml-2 text-[10px] py-0 px-1.5">{m.source}</Badge>
              )}
            </div>
            {/* Cycle-of-pay/collect stamp — when this movement is the
                audit row for a recurring payment or cobro, the backend
                stamps the original cycle's date. Surface it so the user
                knows WHICH cycle was paid (helpful when several cuotas
                of the same series got paid in one month). */}
            {(m.payment_due_date_at_pay || m.receivable_expected_date_at_collect) && (
              <div
                className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1"
                data-testid={`history-cycle-${m.id}`}
              >
                <CalendarClock className="h-3 w-3 text-slate-400" />
                {isEs ? "Ciclo del " : "Cycle of "}
                {new Date(m.payment_due_date_at_pay || m.receivable_expected_date_at_collect).toLocaleDateString(isEs ? "es" : "en", {
                  day: "numeric", month: "short", year: "numeric",
                })}
              </div>
            )}
            {Array.isArray(m.tags) && m.tags.length > 0 && (
              <div className="mt-1.5"><TagChips tags={m.tags} testid={`history-tags-${m.id}`} /></div>
            )}
            {/* iter-169z4 — surface movements that lack a linked cash
                account so the user can add one with a single click.
                Hidden for transfers (always have src+dst). */}
            {/* iter-220.1 — when account/debt IS present, render the
                positive AccountChip; otherwise fall back to the
                existing AccountMissingBadge popover. The duplicate
                "Paid with <debt>" chip below is left in for now
                because it carries directional wording (Recibido en /
                Pagado con) that the generic chip doesn't. */}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <AccountChip
                item={m}
                accounts={cashAccounts}
                debts={debts}
                lang={isEs ? "es" : "en"}
                testid={`history-account-chip-${m.id}`}
                missingFallback={
                  <AccountMissingBadge
                    movement={m}
                    lang={isEs ? "es" : "en"}
                    testid={`history-account-missing-${m.id}`}
                  />
                }
              />
              {/* iter-184-7 / 7c — Positive chip when the movement is
                  funded via a revolving debt (credit card / line of
                  credit). Wording differs by direction: an expense
                  was paid WITH the card (debt grew), an income was
                  received INTO the card (debt was paid down). */}
              {m.source_debt_id && (() => {
                const d = debtById(m.source_debt_id);
                if (!d) return null;
                const isIncome = m.type === "income";
                const label = isIncome
                  ? (isEs ? "Recibido en " : "Received in ")
                  : (isEs ? "Pagado con " : "Paid with ");
                const chipCls = isIncome
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-rose-50 text-rose-700 border-rose-200";
                return (
                  <span
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${chipCls}`}
                    data-testid={`history-paid-with-debt-${m.id}`}
                    title={isIncome
                      ? (isEs ? "Recibido en tarjeta / línea" : "Received into card / line")
                      : (isEs ? "Pagado con tarjeta / línea" : "Paid with card / line")}
                  >
                    <CreditCard className="h-3 w-3" />
                    {label}{d.name}
                  </span>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {m.attachment && (
            <AttachmentBadge attachment={m.attachment} testid={`history-attachment-${m.id}`} />
          )}
          {isTransfer ? (
            // Misma presentación visual que income/expense/cobros/pagos:
            // usa <MoneyAmount/> para que el ≈ y la línea FX rendericen
            // idénticos. El monto principal es lo que LLEGÓ a destino
            // (m.to_amount en dst_currency); para FX, la línea inferior
            // muestra el origen con la tasa aplicada — igual que cobros
            // mostrando original + rate.
            <MoneyAmount
              amount={m.to_amount ?? m.amount}
              currency={dstCurrency}
              original_currency={srcCurrency !== dstCurrency ? srcCurrency : undefined}
              original_amount={srcCurrency !== dstCurrency ? m.amount : undefined}
              fx_rate={m.fx_rate_used}
              fx_at={m.fx_at}
              tone="default"
              size="md"
              testid={`history-transfer-amounts-${m.id}`}
            />
          ) : (
            <MoneyAmount
              amount={m.amount}
              currency={currency}
              original_currency={m.original_currency}
              original_amount={m.original_amount}
              fx_rate={m.fx_rate}
              fx_at={m.fx_at}
              sign={isIncome ? "+" : "-"}
              tone={isIncome ? "income" : "expense"}
              size="md"
              testid={`history-amount-${m.id}`}
            />
          )}
          <Button
            size="icon"
            variant="ghost"
            data-testid={`history-movement-${m.id}-edit-btn`}
            onClick={() => setShowEdit(true)}
            className="hidden sm:inline-flex h-9 w-9 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50"
            title={isEs ? "Editar" : "Edit"}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            data-testid={`history-movement-${m.id}-delete-btn`}
            onClick={() => setConfirmDelete(true)}
            disabled={remove.isPending}
            className="hidden sm:inline-flex h-9 w-9 text-slate-500 hover:text-red-600 hover:bg-red-50"
            title={isEs ? "Eliminar" : "Delete"}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <LineItemsView items={m.line_items} amount={m.amount} currency={currency} reconciled={m.amount_reconciled} claimedAmount={m.claimed_amount} testid={`history-items-${m.id}`} />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid={`delete-confirm-dialog-${m.id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{isEs ? "¿Eliminar este movimiento?" : "Delete this movement?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {isTransfer
                ? (isEs
                    ? `Se eliminará el traslado de ${formatMoney(m.amount, currency)} y se revertirá el efecto en ambas cuentas.`
                    : `The ${formatMoney(m.amount, currency)} transfer will be removed and reversed on both accounts.`)
                : (isEs
                    ? `Se eliminará "${m.description || labelFallback}" por ${isIncome ? "+" : "-"}${formatMoney(m.amount, currency)} y se revertirá su efecto en el saldo.`
                    : `"${m.description || labelFallback}" for ${isIncome ? "+" : "-"}${formatMoney(m.amount, currency)} will be removed and its cash-account effect reversed.`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`delete-confirm-cancel-${m.id}`}>{isEs ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction data-testid={`delete-confirm-ok-${m.id}`} onClick={del} className="bg-red-600 hover:bg-red-700">
              {isEs ? "Eliminar" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <MovementEditDialog
        m={m}
        open={showEdit}
        onClose={() => setShowEdit(false)}
        accounts={cashAccounts}
        isEs={isEs}
        currency={currency}
      />
    </Card>
    </SwipeActions>
  );
}


function CollectedList({ items, currency, lang }) {
  if (!items.length) return <EmptyState lang={lang} kind="collected" />;
  return (
    <div className="grid gap-2" data-testid="history-collected-list">
      {items.map((r) => (
        <Card key={r.id} data-testid={`history-collected-${r.id}`} className="p-3 sm:p-4 rounded-2xl border-slate-200 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-9 w-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
              <Receipt className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate" title={r.client_name}>{r.client_name}</div>
              <div className="text-xs text-slate-500 truncate">
                {tx(lang, "Collected", "Cobrado", "Cobrado")} {new Date(r.updated_at || r.expected_date).toLocaleDateString()}
              </div>
            </div>
          </div>
          <MoneyAmount
            amount={r.amount}
            currency={currency}
            original_currency={r.original_currency}
            original_amount={r.original_amount}
            fx_rate={r.fx_rate}
            fx_at={r.fx_at}
            sign="+"
            tone="income"
            size="md"
            testid={`history-collected-amount-${r.id}`}
          />
        </Card>
      ))}
    </div>
  );
}

function PaidList({ items, currency, lang }) {
  if (!items.length) return <EmptyState lang={lang} kind="paid" />;
  return (
    <div className="grid gap-2" data-testid="history-paid-list">
      {items.map((p) => (
        <Card key={p.id} data-testid={`history-paid-${p.id}`} className="p-3 sm:p-4 rounded-2xl border-slate-200 flex items-center justify-between gap-3 min-w-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-9 w-9 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <Wallet className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate" title={p.concept}>{p.concept}</div>
              <div className="text-xs text-slate-500 truncate">
                {tx(lang, "Paid", "Pagado", "Pagado")} {new Date(p.updated_at || p.due_date).toLocaleDateString()}
                <Badge variant="outline" className="ml-2 text-[10px] py-0 px-1.5">{p.type}</Badge>
              </div>
            </div>
          </div>
          <MoneyAmount
            amount={p.amount}
            currency={currency}
            original_currency={p.original_currency}
            original_amount={p.original_amount}
            fx_rate={p.fx_rate}
            fx_at={p.fx_at}
            sign="-"
            tone="expense"
            size="md"
            testid={`history-paid-amount-${p.id}`}
          />
        </Card>
      ))}
    </div>
  );
}
